import { parseISO } from 'date-fns'

import { CaseSorting } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatOptionalDateRange, Tooltip } from '@avantstay/backoffice-core'
import { SortDirection } from '@avantstay/data-grid'

import { SearchFilter } from '@new/__global/components/Filters'
import { FilterConfig } from '@new/__global/components/FiltersFramework'
import {
  arrayParamsFormatterAndParser,
  booleanParamParser,
} from '@new/__global/components/FiltersFramework/_utils'
import { RegionsFilter } from '@new/__global/components/RegionsFilter'
import {
  AiGeneratedFilter,
  AirbnbTripIssueFilter,
  CaseOwnersFilter,
  CaseVendorsFilter,
  CategoriesFilter,
  CreatedByFilter,
  GuestReadinessStatusesFilter,
  HasExpenseFilter,
  InStayGuestIssueFilter,
  PhotoCompletionFilter,
  PriorityFilter,
  PropertyStatusFilter,
  RecordTypeFilter,
  RecurringFilter,
  StatusFilter,
  SubcategoriesFilter,
  TypeFilter,
} from '@new/domains/operations/screens/CasesV2/components/CasesFilters/ColumnFilters'
import {
  CreatedAtFilter,
  DueDateFilter,
} from '@new/domains/operations/screens/CasesV2/components/CasesFilters/ColumnFilters/DateFilters'
import { QuickFilter } from '@new/domains/operations/screens/CasesV2/components/CasesFilters/QuickFilter'
import { ShowOffboardedFilter } from '@new/domains/operations/screens/CasesV2/components/CasesFilters/ToggleFilters/ShowOffboardedFilter'
import { parseLabel } from '@new/domains/operations/utils/parseLabel'

import { QuickFilterOptions } from '../CasesV2.types'
import { AssignedFOFilter } from '../components/CasesFilters/ColumnFilters/AssginedFOFilter'
import { PropertyFilter } from '../components/CasesFilters/ColumnFilters/PropertyFilter'
import {
  AssignedFOPreviewFormatter,
  CaseOwnersPreviewFormatter,
  CaseVendorsPreviewFormatter,
  CreatedByPreviewFormatter,
  PropertyPreviewFormatter,
  StringListPreviewFormatter,
} from '../components/PreviewFilterFormatter'

export const CASES_GRID_ID = 'cases-v2-grid'

export enum CaseFilterIds {
  SortBy = 'sortBy',
  SortDirection = 'sortOrder',
  Page = 'page',
  PageSize = 'pageSize',
  Regions = 'regionIds',
  Search = 'searchTerm',
  CaseNumber = 'caseNumber',
  Priority = 'priorities',
  RecordType = 'recordTypes',
  Type = 'types',
  Category = 'categories',
  Subcategory = 'subcategories',
  TaxonomyNodes = 'taxonomyNodeIds',
  DueDate = 'dueDate',
  DueTime = 'dueTime',
  Status = 'statuses',
  Subject = 'subject',
  Property = 'homeIds',
  CaseOwner = 'caseOwnerIds',
  CaseVendor = 'caseVendorIds',
  ShowOffboarded = 'showOffboarded',
  InStayGuestIssue = 'inStayGuestIssue',
  Recurring = 'isRecurringCaseStatus',
  AirbnbTripIssue = 'isAirbnbTripIssuesOnly',
  HasExpense = 'hasExpense',
  CreateDate = 'createdAtPeriod',
  CreateTime = 'createTime',
  CreatedBy = 'creatorIds',
  QuickFilter = 'quickFilter',
  AssignedFO = 'assignedFieldOpsIds',
  PhotoCompletion = 'photoCompletions',
  AiGenerated = 'aiGenerated',
  GuestReadinessStatus = 'guestReadinessStatuses',
  PropertyStatus = 'managedUnitAvailabilityStatuses',
  Market = 'Market',
}

export { CaseColumnKeys } from './caseColumnKeys'

// Used to display header
export enum CaseColumnNames {
  SortBy = 'Sort By',
  SortDirection = 'Sort Direction',
  CaseNumber = 'Case Number',
  Priority = 'Priority',
  RecordType = 'Record Type',
  Type = 'Type',
  Category = 'Category',
  Subcategory = 'Subcategory',
  DueDate = 'Due Date',
  Status = 'Status',
  Subject = 'Subject',
  Property = 'Property',
  OpenCases = 'Property Cases',
  CaseOwner = 'Case Owner',
  CaseVendor = 'Assigned Vendor',
  InStayGuestIssue = 'ISGI?',
  Recurring = 'Recurring?',
  AirbnbTripIssue = 'Airbnb Trip Issue?',
  HasExpense = 'Has Expense?',
  CreateDate = 'Create Date',
  CreateTime = 'Create Time',
  CreatedBy = 'Created By',
  DueTime = 'Due Time',
  SpendThreshold = 'Spend Threshold',
  MtMarkup = 'MT Markup %',
  Age = 'Case Age (In Days)',
  AssignedFo = 'Assigned FO',
  OriginalDueDate = 'Original Due Date',
  RevenueModel = 'Revenue Model',
  OwnerNps = 'Owner NPS',
  GuestNps = 'Guest NPS',
  GuestAirbnbRating = 'Guest Airbnb Rating',
  GuestVrboRating = 'Guest Vrbo Rating',
  PhotoCompletion = 'Photo Completion',
  AiGenerated = 'AI Generated',
  GuestReadinessStatus = 'Guest Readiness Status',
  PhotoPercentage = 'Photo %',
  CheckListPercentage = 'Checklist %',
  Review = 'Clean/Inspection Report Link',
  PropertyStatus = 'Property Status',
  ValueAddedServices = 'VAS',
  Market = 'Market',
  BookingRating = 'Booking Rating',
  BookingNumber = 'Booking Number',
  CheckInDate = 'Arrival Date',
  CheckOutDate = 'Departure Date',
  GuestName = 'Guest Name',
  NightsCount = 'Nights',
  GuestsCount = 'Guests',
  BookingSource = 'Booking Source',
}

const columnFilterProps = {
  showOnTheTopFiltersBar: () => false,
  showOnTheSidePanel: () => false,
}

export const casesFiltersConfig: FilterConfig<any>[] = [
  {
    id: CaseFilterIds.QuickFilter,
    label: 'Quick View',
    queryParamName: 'quick-filter',
    showOnTheTopFiltersBar: () => false,
    showOnTheSidePanel: () => true,
    component: props => <QuickFilter {...props} />,
    defaultValue: QuickFilterOptions.all,
  },
  // Sorting
  {
    id: CaseFilterIds.SortBy,
    label: CaseColumnNames.SortBy,
    queryParamName: 'sort-by',
    ...columnFilterProps,
    defaultValue: CaseSorting.createdAt,
  },
  {
    id: CaseFilterIds.SortDirection,
    label: CaseColumnNames.SortDirection,
    queryParamName: 'sort-direction',
    ...columnFilterProps,
    showOnTheAppliedFilters: () => false,
    defaultValue: SortDirection.Desc,
  },

  // Pagination
  {
    id: CaseFilterIds.Page,
    label: 'Page',
    queryParamName: 'page',
    ...columnFilterProps,
    showOnTheAppliedFilters: () => false,
    defaultValue: 1,
  },
  {
    id: CaseFilterIds.PageSize,
    label: 'Page Size',
    queryParamName: 'page-size',
    ...columnFilterProps,
    showOnTheAppliedFilters: () => false,
    defaultValue: 30,
  },

  // Header Filters
  {
    id: CaseFilterIds.Regions,
    label: 'regions',
    queryParamName: 'regions',
    showOnTheTopFiltersBar: () => true,
    showOnTheSidePanel: () => false,
    component: props => <RegionsFilter {...props} />,
    ...arrayParamsFormatterAndParser,
  },
  {
    id: CaseFilterIds.Search,
    label: 'search',
    queryParamName: 'search',
    showOnTheTopFiltersBar: () => true,
    component: props => (
      <Tooltip
        content="Search by case subject, property, case owner, vendor, category and subcategory"
        placement="top"
      >
        <div>
          <SearchFilter
            {...props}
            placeholder="Search"
            iconSearchSize={{ width: 16, height: 16 }}
            width={200}
          />
        </div>
      </Tooltip>
    ),
  },
  {
    id: CaseFilterIds.ShowOffboarded,
    label: 'Show Only Offboarded Homes',
    queryParamName: 'show-offboarded',
    showOnTheTopFiltersBar: () => false,
    showOnTheSidePanel: () => true,
    filterPreviewFormatter: (value: boolean) => (value ? 'Yes' : 'No'),
    component: props => <ShowOffboardedFilter {...props} />,
    ...booleanParamParser,
    defaultValue: undefined,
  },

  // Column Filters

  {
    id: CaseFilterIds.RecordType,
    label: CaseColumnNames.RecordType,
    queryParamName: 'record-types',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <RecordTypeFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.Priority,
    label: CaseColumnNames.Priority,
    queryParamName: 'priorities',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <PriorityFilter {...props} />,
  },
  {
    id: CaseFilterIds.Type,
    label: CaseColumnNames.Type,
    queryParamName: CaseFilterIds.Type,
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: () => <TypeFilter />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.Category,
    label: CaseColumnNames.Category,
    queryParamName: 'categories',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: () => <CategoriesFilter />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.Subcategory,
    label: CaseColumnNames.Subcategory,
    queryParamName: 'subcategory',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: () => <SubcategoriesFilter />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
  {
    // The four taxonomy column filters share this single param, mirroring the
    // searchCases arg. Applied-filter chips for it are merged into the legacy
    // column chips by CasesFiltersStore.filtersForPreview.
    id: CaseFilterIds.TaxonomyNodes,
    label: 'New Taxonomy',
    queryParamName: 'taxonomy-node-ids',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    showOnTheAppliedFilters: () => false,
  },
  {
    id: CaseFilterIds.DueDate,
    label: CaseColumnNames.DueDate,
    queryParamName: 'due-date',
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: props => <DueDateFilter {...props} />,
    filterPreviewFormatter: (value: string) => {
      const [from, to] = value.split('_')
      return formatOptionalDateRange(parseISO(from), parseISO(to))
    },
  },
  {
    id: CaseFilterIds.Status,
    label: CaseColumnNames.Status,
    queryParamName: 'status',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <StatusFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.Property,
    label: CaseColumnNames.Property,
    queryParamName: 'properties',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <PropertyFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <PropertyPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.CaseOwner,
    label: CaseColumnNames.CaseOwner,
    queryParamName: 'case-owners',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <CaseOwnersFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <CaseOwnersPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.CaseVendor,
    label: CaseColumnNames.CaseVendor,
    queryParamName: 'case-vendors',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <CaseVendorsFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <CaseVendorsPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.CreateDate,
    label: CaseColumnNames.CreateDate,
    queryParamName: 'created-at',
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: props => <CreatedAtFilter {...props} />,
    filterPreviewFormatter: (value: string) => {
      const [from, to] = value.split('_')
      return formatOptionalDateRange(parseISO(from), parseISO(to))
    },
  },
  {
    id: CaseFilterIds.CreatedBy,
    label: CaseColumnNames.CreatedBy,
    queryParamName: 'created-by',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: () => <CreatedByFilter />,
    filterPreviewFormatter: (value: string[]) => <CreatedByPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.InStayGuestIssue,
    label: CaseColumnNames.InStayGuestIssue,
    queryParamName: 'in-stay-guest-issue',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <InStayGuestIssueFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.Recurring,
    label: CaseColumnNames.Recurring,
    queryParamName: 'recurring',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <RecurringFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.AirbnbTripIssue,
    label: CaseColumnNames.AirbnbTripIssue,
    queryParamName: 'airbnb-trip-issues',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <AirbnbTripIssueFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.HasExpense,
    label: CaseColumnNames.HasExpense,
    queryParamName: 'has-expense',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <HasExpenseFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.AssignedFO,
    label: CaseColumnNames.AssignedFo,
    queryParamName: 'assigned-fo',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <AssignedFOFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <AssignedFOPreviewFormatter values={value} />,
  },
  {
    id: CaseFilterIds.PhotoCompletion,
    label: CaseColumnNames.PhotoCompletion,
    queryParamName: 'photo-completion',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <PhotoCompletionFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.AiGenerated,
    label: CaseColumnNames.AiGenerated,
    queryParamName: 'ai-generated',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    filterPreviewNoCapitalization: true,
    component: () => <AiGeneratedFilter />,
    filterPreviewFormatter: (value: string[]) => value.map(v => parseLabel(v)).join(', '),
  },
  {
    id: CaseFilterIds.GuestReadinessStatus,
    label: CaseColumnNames.GuestReadinessStatus,
    queryParamName: 'guest-readiness-statuses',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <GuestReadinessStatusesFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },

  {
    id: CaseFilterIds.PropertyStatus,
    label: CaseColumnNames.PropertyStatus,
    queryParamName: 'property-statuses',
    ...arrayParamsFormatterAndParser,
    ...columnFilterProps,
    component: props => <PropertyStatusFilter {...props} />,
    filterPreviewFormatter: (value: string[]) => <StringListPreviewFormatter values={value} />,
  },
]
